/* eslint-disable import/no-internal-modules */
import { Lazy } from '@studio/js-utils/async';
import { generateId } from '@studio/models';
import type { AppId } from '@studio/models/app';
import type { Store } from '@studio/react-utils/store';
import type { T_Any } from '@tryiris-ai/js-types';
import { freeze } from 'immer';
import { immer } from 'zustand/middleware/immer';
import { createStore } from 'zustand/vanilla';
import { AppPrefix } from './constants.js';
import { INITIAL_BOOT_OBSERVATION, type Draft, type State } from './types.js';
import { FastFrameSelectStage } from '#~/control/components/interaction/fast-frame/types.js';
import type { AutoLinkState } from '#~/routes/verify-auto-link/v2/auto-link-types.js';

/** Lazily loaded data depot. */
const __depot = new Lazy(
  (): Store<State> =>
    createStore<State>()(
      immer((set, _get) =>
        freeze({
          account: undefined,
          agents: {},
          appId: <AppId>`${AppPrefix}${generateId().slice(AppPrefix.length)}`,
          autoTrackingInstances: {},
          liveKit: {},
          network: {
            isConnected: true,
            isOnline: true
          },
          notifications: {},
          // Per Multi-Add Phase 2: records which form fields were
          // auto-populated by Extended Discovery or the ONVIF probe,
          // so `<AutoConfiguredLabel>` can render a small green tag
          // beside each one. Cleared implicitly on full page reload
          // (session-only — cross-session persistence is a later-phase
          // follow-up).
          autoConfiguredFields: {},
          // Post-add origin tracking. Session-scoped, initialized
          // empty. Per `think-in-systems` (ai.knowledge/global):
          // client-only primitive, no protobuf / no state-sync. See
          // `notifications/multi-add-origins.ts` for the helper API.
          multiAddOriginByDeviceId: {},
          // Per-device last-posted desired-alert identity (ENG-4486
          // declarative reconciliation). Session-scoped, initialized empty.
          // Keyed by `deviceId`; value is the live alert's toast id.
          lastDesiredAlertByDevice: {},
          connectionFailureHoldByDevice: {},
          // Per-(device, sub-tab) dismissal map for the Advanced Settings
          // "needs attention" warning icons (Multi-Add Phase 2).
          // See knowledge: multi-writer-registry-completeness
          // (ai.knowledge/platform/studio.client) — every depot init
          // path must populate the slice so consumers can index
          // without a `?? {}` guard.
          dismissedNeedAttentionTabs: {},
          // Per-(device, sub-tab) analytics-emit dedup set (Multi-Add
          // Phase 2). Session-scoped, initialized empty. Keyed
          // `<deviceId>:<tab>`. Records which
          // `MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN` events have fired.
          shownSubtabAnalytics: {},
          produce: (fn: (draft: Draft) => void): void => {
            set(fn);
          },
          registry: {
            brands: undefined,
            models: {},
            templates: {}
          },
          rooms: {},
          roomRecords: {},
          roomInviteRecords: [],
          roomMembershipRecords: [],
          organizations: {},
          synchronizer: {
            hasRegistryLoaded: false,
            isRegistryLoading: false
          },
          // Pre-warm: if Electron reports no database.db (fresh install), start
          // with 'linking' state so the BootOverlay shows "Setting up your bridge…"
          // from the first frame. The autolink controller will overwrite this.
          boot: {
            autoLinkState: { status: 'idle' },
            isRootReady: false,
            isOverlayVisible: true,
            error: null,
            observation: INITIAL_BOOT_OBSERVATION
          },
          toolStates: {
            fastFrameInitialScale: -1,
            fastFrameOnExitTool: undefined,
            fastFrameSelectStage: FastFrameSelectStage.INITIAL,
            toolInitialPosition: undefined
          },
          user: undefined
        })
      )
    )
);

/** Data depot proxy. */
export const depot = new Proxy<Store<State>>(<T_Any>{}, {
  get(...args): unknown {
    return Reflect.get(__depot.value, args[1], args[2]);
  }
});

/**
 * y4fp5-W2 fix: refresh-locked loader. On a Cmd-R refresh of a
 * deep URL like `/room/:id`, neither `<Root>` nor `<AutoLinkGuard>`
 * mounts — both only wrap the index route. ProfileSynchronizer is
 * the only protected-layout writer that runs on every URL, so it
 * takes over publishing the two BootOverlay dismiss signals
 * (`boot.isRootReady`, `boot.autoLinkState`) that would otherwise
 * be stuck at their initial values forever, leaving the spinner up.
 *
 * On the index route this is a no-op: Root + AutoLinkGuard ARE
 * mounted and own their slices. The idle-guard on `autoLinkState`
 * mirrors AutoLinkGuard's own bypass effect (auto-link-guard.tsx
 * ~L198-202) — defensive against a future refactor that mounts the
 * controller elsewhere.
 *
 * See artifacts/y4fp5-W2-refresh-unlock/.
 */
export const applyRefreshUnlockedBootDismiss = (
  draft: { boot: { autoLinkState: AutoLinkState; isRootReady: boolean } },
  pathname: string
): void => {
  if (pathname === '/' || pathname === '') return;
  draft.boot.isRootReady = true;
  if (draft.boot.autoLinkState.status === 'idle') {
    draft.boot.autoLinkState = { status: 'skipped', reason: 'not_electron' };
  }
};
